<script>
import { crudMethods } from '@state/helpers'
import IkhtisarTable from './ikhtisar-table.vue'
import PanjarIkhtisarTable from './panjar-ikhtisar-table.vue'
import UploadExcelModal from './upload-excel-modal.vue'
import IkhtisarViewModal from './ikhtisar-view-modal.vue'

export default {
  name: 'CompIkhtisar',

  components: {
    IkhtisarTable,
    PanjarIkhtisarTable,
    UploadExcelModal,
    IkhtisarViewModal,
  },

  props: {
    proyek: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },

  data() {
    return {
      title: 'Rekonsiliasi Panjar melalui Ikhtisar',

      proyekId: this.$route.params.id ? this.$route.params.id : '',

      kodeProyekIni: this.$route.params.id ? this.$route.params.id : '',

      loading: false,
      isDownloading: false,

      // =========================================================
      // DATA PANJAR
      // =========================================================
      listPanjar: [],

      // =========================================================
      // DATA IKHTISAR
      // Tetap dipakai untuk daftar dokumen ikhtisar
      // =========================================================
      listItems: [],

      // =========================================================
      // MODAL
      // =========================================================
      modalUploadVisible: false,
      modalManualVisible: false,
      modalPreviewVisible: false,

      selectedIkhtisarHdId: null,

      dataIkhtisarHd: {},
      dataProyek: {},
      listDetailsPanjar: [],
      listTagihansDb: [],

      // =========================================================
      // ERROR
      // =========================================================
      modalError: false,
      messageError: '',

      counter: 0,
    }
  },

  computed: {
    // =========================================================
    // API
    // =========================================================
    apiPanjarUrl() {
      return `/web/transaksi/proyek/${this.proyekId}/panjar`
    },

    apiIkhtisarUrl() {
      return `/web/transaksi/proyek/${this.proyekId}/panjar-ikhtisar`
    },

    // =========================================================
    // TOTAL PANJAR
    // =========================================================
    totalPanjar() {
      return this.listPanjar.reduce((total, item) => {
        return total + this.hitungTotalPanjar(item)
      }, 0)
    },

    // =========================================================
    // TOTAL YANG SUDAH DIREKONSILIASI / DIIKHTISARKAN
    //
    // Sumber:
    // item.ikhtisars[].nominal
    // =========================================================
    totalDiikhtisarkan() {
      return this.listPanjar.reduce((total, item) => {
        return total + this.hitungTotalIkhtisar(item)
      }, 0)
    },

    // =========================================================
    // SISA PANJAR
    // =========================================================
    sisaPanjar() {
      return Math.max(this.totalPanjar - this.totalDiikhtisarkan, 0)
    },

    // =========================================================
    // PROGRESS
    // =========================================================
    progressIkhtisar() {
      if (!this.totalPanjar) {
        return 0
      }

      const progress = (this.totalDiikhtisarkan / this.totalPanjar) * 100

      return Math.min(Math.max(progress, 0), 100)
    },

    // =========================================================
    // PERSENTASE SISA
    // =========================================================
    progressSisa() {
      return Math.max(100 - this.progressIkhtisar, 0)
    },
  },

  mounted() {
    if (this.proyekId !== '') {
      this.fetchData()
    } else {
      this.messageError = 'Parameter Proyek ID tidak ditemukan di URL.'

      this.modalError = true
    }
  },

  methods: {
    ...crudMethods,
    openManualIkhtisar() {
      this.modalManualVisible = true
    },
    closeError() {
      this.modalError = false
    },

    // =========================================================
    // LOAD SEMUA DATA
    // =========================================================
    fetchData() {
      this.loading = true

      Promise.all([this.fetchPanjarData(), this.fetchIkhtisarData()])
        .catch((err) => {
          this.handleErrorResponse(
            err,
            'Gagal mengambil data rekonsiliasi panjar.'
          )
        })
        .finally(() => {
          this.loading = false
        })
    },

    // =========================================================
    // GET DATA PANJAR
    //
    // INI SEKARANG MENJADI SUMBER UTAMA TAB IKHTISAR
    // =========================================================
    fetchPanjarData() {
      return this.listData({
        url: this.apiPanjarUrl,
      }).then((response) => {
        const responseData = response.data || {}
        const data = responseData.data

        this.listPanjar = Array.isArray(data)
          ? data
          : data && Array.isArray(data.data)
          ? data.data
          : []
      })
    },

    // =========================================================
    // GET DATA IKHTISAR
    //
    // Tetap dipakai untuk daftar DOC-1, DOC-2, dst.
    // =========================================================
    fetchIkhtisarData() {
      return this.listData({
        url: this.apiIkhtisarUrl,
      }).then((response) => {
        const responseData = response.data || {}
        const data = responseData.data

        this.listItems = Array.isArray(data)
          ? data
          : data && Array.isArray(data.data)
          ? data.data
          : []
      })
    },

    // =========================================================
    // HITUNG BIAYA LAIN PANJAR
    // =========================================================
    hitungBiayaLain(biayaLains) {
      if (!Array.isArray(biayaLains)) {
        return 0
      }

      return biayaLains.reduce((total, item) => {
        return total + Number(item.biaya || 0)
      }, 0)
    },

    // =========================================================
    // HITUNG TOTAL PANJAR
    //
    // DPP + PPN + BIAYA LAIN
    //
    // Sama dengan logic existing PanjarTable.
    // =========================================================
    hitungTotalPanjar(item) {
      if (!item) {
        return 0
      }

      const dpp = Number(item.nilai_dpp || 0)

      const ppn = (dpp * Number(item.ppn || 0)) / 100

      const biayaLain = this.hitungBiayaLain(item.biaya_lains)

      return dpp + ppn + biayaLain
    },

    // =========================================================
    // HITUNG TOTAL IKHTISAR PER PANJAR
    //
    // 1 panjar bisa memiliki lebih dari 1 ikhtisar.
    //
    // Contoh:
    //
    // ikhtisars:
    // [
    //   { nominal: 1.000.000 },
    //   { nominal:   500.000 }
    // ]
    //
    // hasil = 1.500.000
    // =========================================================
    hitungTotalIkhtisar(item) {
      if (!item || !Array.isArray(item.ikhtisars)) {
        return 0
      }

      return item.ikhtisars.reduce((total, ikhtisar) => {
        return total + Number(ikhtisar.nominal || 0)
      }, 0)
    },

    // =========================================================
    // FORMAT RUPIAH
    // =========================================================
    formatRupiah(value) {
      if (value === undefined || value === null || value === '') {
        return '0'
      }

      const numericValue = Number(value)

      if (isNaN(numericValue)) {
        return '0'
      }

      return numericValue
        .toFixed(0)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },

    // =========================================================
    // FORMAT PERSENTASE
    // =========================================================
    formatPercent(value) {
      return Number(value || 0).toFixed(2)
    },

    // =========================================================
    // OPEN DETAIL IKHTISAR
    // =========================================================
    openPreviewEdit(id) {
      this.selectedIkhtisarHdId = id

      this.loading = true

      this.listData({
        url: `/web/transaksi/proyek/${this.proyekId}/panjar-ikhtisar/${id}/edit`,
      })
        .then((response) => {
          const resBody = response.data || {}
          const resData = resBody.data || {}

          this.dataIkhtisarHd = resData.header || {}

          this.dataProyek = resData.proyek || {}

          this.listDetailsPanjar = resData.details || []

          this.listTagihansDb = resData.tagihans_db || []

          this.modalPreviewVisible = true
        })
        .catch((err) => {
          this.handleErrorResponse(
            err,
            'Gagal memuat data detail kombinasi ikhtisar.'
          )
        })
        .finally(() => {
          this.loading = false
        })
    },

    // =========================================================
    // DOWNLOAD EXCEL IKHTISAR
    // =========================================================
    downloadExportExcel(id) {
      this.isDownloading = true

      this.toast(
        'b-toaster-bottom-right',
        true,
        'Sedang menyiapkan unduhan berkas excel...',
        'info'
      )

      this.downloadData({
        url: `/web/transaksi/proyek/${this.proyekId}/panjar-ikhtisar/${id}/export-excel`,
      })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]))

          const link = document.createElement('a')

          link.href = url

          const namaFile = `Ikhtisar_Rekonsiliasi_${this.proyek.kode_proyek ||
            'Panjar'}.xlsx`

          link.setAttribute('download', namaFile)

          document.body.appendChild(link)

          link.click()

          document.body.removeChild(link)

          window.URL.revokeObjectURL(url)
        })
        .catch((err) => {
          this.toast(
            'b-toaster-bottom-right',
            true,
            'Gagal mengunduh file laporan export excel.',
            'danger'
          )

          console.error(err)
        })
        .finally(() => {
          this.isDownloading = false
        })
    },

    // =========================================================
    // APPROVE IKHTISAR
    // =========================================================
    handleApproveIkhtisar(id) {
      this.$bvModal
        .msgBoxConfirm(
          'Setujui (Approve) dokumen ikhtisar ini? Status tidak dapat dikembalikan ke draft setelah disetujui.',
          {
            title: 'Mohon Konfirmasi',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'success',
            okTitle: 'YA',
            cancelTitle: 'TIDAK',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then((value) => {
          if (!value) {
            return
          }

          const urlApprove = `/web/transaksi/proyek/${this.proyekId}/panjar-ikhtisar/${id}/approve`

          this.updateData({
            url: urlApprove,
            body: {},
          })
            .then(() => {
              // Refresh dua sumber data.
              this.fetchData()

              this.toast(
                'b-toaster-bottom-right',
                true,
                'Berhasil Disetujui (APPROVED)',
                'success'
              )
            })
            .catch((err) => {
              let errMessage = 'Gagal menyetujui dokumen.'

              if (err.response && err.response.data) {
                errMessage = err.response.data.message || errMessage
              }

              this.toast(
                'b-toaster-bottom-right',
                true,
                `Tidak Dapat Disetujui: ${errMessage}`,
                'danger'
              )
            })
        })
    },

    // =========================================================
    // TAMBAH IKHTISAR
    // =========================================================
    tambahIkhtisar() {
      if (this.proyekId === '') {
        this.messageError = 'Tidak dapat menambah data karena Proyek ID kosong.'

        this.modalError = true

        return
      }

      this.modalUploadVisible = true
    },

    // =========================================================
    // UPLOAD EXCEL
    // =========================================================
    handleSaveExcel({ formData, onSuccess, onError }) {
      const urlStoreExcel = `/web/transaksi/proyek/${this.proyekId}/panjar-ikhtisar/store-excel`

      this.insertData({
        url: urlStoreExcel,
        body: formData,
      })
        .then(() => {
          onSuccess()

          // Penting:
          // setelah upload ikhtisar, refresh Panjar
          // supaya ikhtisars[] terbaru langsung masuk
          // ke summary dan tabel.
          this.fetchData()

          this.toast(
            'b-toaster-bottom-right',
            true,
            'File Excel Berhasil Diproses',
            'success'
          )
        })
        .catch((err) => {
          onError()

          let errMessage = 'Gagal memproses validasi excel.'

          if (err.response && err.response.data) {
            errMessage = err.response.data.message || errMessage
          }

          this.toast(
            'b-toaster-bottom-right',
            true,
            `Proses Excel Gagal: ${errMessage}`,
            'danger'
          )
        })
    },

    async handleSaveManual({ formData, onSuccess, onError }) {
      try {
        const response = await this.insertData({
          url: `${this.apiIkhtisarUrl}/store-manual`,
          body: formData,
          throwOnError: true,
        })
        if (!response || !response.data.success) {
          throw new Error('Gagal menyimpan ikhtisar manual. Silakan periksa data dan coba lagi.')
        }
        onSuccess()
        this.fetchData()
        this.toast('b-toaster-bottom-right', true, 'Ikhtisar manual berhasil disimpan sebagai DRAFT.', 'success')
      } catch (error) {
        onError(error)
      }
    },

    // =========================================================
    // UPDATE DETAIL PANJAR DARI MODAL IKHTISAR
    // =========================================================
    handleUpdatePanjarDetails({ details, onSuccess, onError }) {
      const urlUpdateDetails = `/web/transaksi/proyek/${this.proyekId}/panjar-ikhtisar/${this.selectedIkhtisarHdId}/update`

      this.updateData({
        url: urlUpdateDetails,
        body: {
          details,
        },
      })
        .then(() => {
          onSuccess()

          // Refresh Panjar + Ikhtisar.
          this.fetchData()

          this.toast(
            'b-toaster-bottom-right',
            true,
            'Berhasil Disimpan',
            'success'
          )
        })
        .catch((err) => {
          onError()

          let errMessage = 'Gagal menyimpan perubahan panjar.'

          if (err.response && err.response.data) {
            errMessage = err.response.data.message || errMessage
          }

          this.toast(
            'b-toaster-bottom-right',
            true,
            `Gagal Disimpan: ${errMessage}`,
            'danger'
          )
        })
    },

    // =========================================================
    // TOAST
    // =========================================================
    toast(toaster, append = false, body, variant) {
      this.counter++

      this.$bvToast.toast(`Dokumen ${this.title} ${body}`, {
        title: `Notifikasi ${this.title}`,
        toaster,
        solid: true,
        appendToast: append,
        variant,
      })
    },

    // =========================================================
    // ERROR HANDLER
    // =========================================================
    handleErrorResponse(err, defaultMessage) {
      if (
        err.response &&
        err.response.data &&
        typeof err.response.data === 'object'
      ) {
        this.messageError = err.response.data.message || defaultMessage
      } else {
        this.messageError =
          defaultMessage || 'Terjadi kesalahan saat memuat data dari server.'
      }

      this.modalError = true
    },
  },
}
</script>

<template>
  <div>
    <!-- ===================================================== -->
    <!-- HEADER -->
    <!-- ===================================================== -->
    <div class="row mb-3 align-items-center">
      <div class="col-sm-6">
        <h5 class="m-0 font-weight-bold text-dark">
          {{ title }}
        </h5>

        <small class="text-muted">
          Progress penutupan panjar berdasarkan dokumen ikhtisar.
        </small>
      </div>

      <div class="d-flex col-sm-6 justify-content-end">
        <button
          type="button"
          class="btn btn-outline-primary mr-2 shadow-sm"
          :disabled="loading || isDownloading"
          @click="openManualIkhtisar"
        >
          <i class="fa fa-edit mr-1"></i> Tambah Manual
        </button>
        <button
          class="btn btn-primary d-flex flex-col justify-content-center align-items-center height-36-px shadow-sm"
          :disabled="isDownloading"
          @click="tambahIkhtisar"
        >
          <span class="d-flex align-items-center gap-5 width-100">
            <i class="fa fa-plus-circle mr-1"></i>
            Tambah via Excel
          </span>
        </button>
      </div>
    </div>

    <!-- ===================================================== -->
    <!-- LOADING -->
    <!-- ===================================================== -->
    <div v-if="loading || isDownloading" class="text-center my-5">
      <b-spinner variant="primary" label="Spinning"></b-spinner>

      <p class="text-muted mt-2">
        {{
          isDownloading
            ? 'Sedang memproses berkas pengunduhan laporan excel...'
            : 'Memuat data rekonsiliasi panjar...'
        }}
      </p>
    </div>

    <div v-else class="col-sm-12 p-0">
      <!-- =================================================== -->
      <!-- SUMMARY -->
      <!-- =================================================== -->
      <div class="reconciliation-summary mb-4">
        <!-- TOTAL PANJAR -->
        <div class="summary-item">
          <div class="summary-icon icon-primary">
            <i class="uil uil-file-alt"></i>
          </div>

          <div class="summary-content">
            <div class="summary-label">
              Total Panjar
            </div>

            <div class="summary-value">
              Rp {{ formatRupiah(totalPanjar) }}
            </div>
          </div>
        </div>

        <!-- SUDAH DIIKHTISARKAN -->
        <div class="summary-item">
          <div class="summary-icon icon-success">
            <i class="uil uil-check-circle"></i>
          </div>

          <div class="summary-content">
            <div class="summary-label">
              Sudah Diikhtisarkan
            </div>

            <div class="summary-value text-success">
              Rp {{ formatRupiah(totalDiikhtisarkan) }}
            </div>

            <div class="summary-percent text-success">
              {{ formatPercent(progressIkhtisar) }}%
            </div>
          </div>
        </div>

        <!-- SISA PANJAR -->
        <div class="summary-item">
          <div class="summary-icon icon-warning">
            <i class="uil uil-clock"></i>
          </div>

          <div class="summary-content">
            <div class="summary-label">
              Sisa Panjar
            </div>

            <div class="summary-value text-danger">
              Rp {{ formatRupiah(sisaPanjar) }}
            </div>

            <div class="summary-percent text-danger">
              {{ formatPercent(progressSisa) }}%
            </div>
          </div>
        </div>

        <!-- PROGRESS -->
        <div class="summary-progress">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="summary-label">
              Progress Penutupan Panjar
            </span>

            <strong class="summary-progress-value">
              {{ formatPercent(progressIkhtisar) }}%
            </strong>
          </div>

          <div class="progress" style="height: 12px;">
            <div
              class="progress-bar bg-success"
              role="progressbar"
              :style="{
                width: `${progressIkhtisar}%`,
              }"
              :aria-valuenow="progressIkhtisar"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>

          <small class="text-muted d-block mt-2">
            Rp {{ formatRupiah(totalDiikhtisarkan) }} dari Rp
            {{ formatRupiah(totalPanjar) }}
          </small>
        </div>
      </div>

      <!-- =================================================== -->
      <!-- TABEL PANJAR + REKONSILIASI -->
      <!-- =================================================== -->
      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 class="font-weight-bold text-dark mb-1">
              Daftar Panjar & Rekonsiliasi Ikhtisar
            </h5>

            <small class="text-muted">
              Data berdasarkan panjar proyek dan realisasi yang sudah masuk ke
              dokumen ikhtisar.
            </small>
          </div>
        </div>

        <panjar-ikhtisar-table
          :items="listPanjar"
          :ikhtisars="listItems"
          @document-click="openPreviewEdit"
        />
      </div>

      <!-- =================================================== -->
      <!-- DAFTAR IKHTISAR -->
      <!-- =================================================== -->
      <div class="mt-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="font-weight-bold text-dark m-0">
            Daftar Ikhtisar Rekonsiliasi Panjar
          </h5>
        </div>

        <ikhtisar-table
          :items="listItems"
          @action-click="openPreviewEdit"
          @approve-click="handleApproveIkhtisar"
          @download-click="downloadExportExcel"
        />
      </div>
    </div>

    <!-- ===================================================== -->
    <!-- UPLOAD -->
    <!-- ===================================================== -->
    <upload-excel-modal
      v-model="modalManualVisible"
      :kode-proyek="kodeProyekIni"
      mode="manual"
      @submit-upload="handleSaveManual"
    />
    <upload-excel-modal
      v-model="modalUploadVisible"
      :kode-proyek="kodeProyekIni"
      @submit-upload="handleSaveExcel"
    />

    <!-- ===================================================== -->
    <!-- VIEW IKHTISAR -->
    <!-- ===================================================== -->
    <ikhtisar-view-modal
      v-model="modalPreviewVisible"
      :ikhtisar-hd="dataIkhtisarHd"
      :proyek="dataProyek"
      :details="listDetailsPanjar"
      :tagihans-db="listTagihansDb"
      @save-changes="handleUpdatePanjarDetails"
    />

    <!-- ===================================================== -->
    <!-- ERROR -->
    <!-- ===================================================== -->
    <b-modal v-model="modalError" centered hide-header hide-footer>
      <div class="p-3 text-center">
        <div class="text-danger display-4 mb-3">
          <i class="mdi mdi-alert-circle-outline"></i>
        </div>

        <h5>Terjadi Kendala</h5>

        <p class="text-danger small bg-light p-2">
          {{ messageError }}
        </p>

        <button
          class="btn btn-secondary btn-sm btn-block mt-2 rounded-0"
          @click="closeError"
        >
          Tutup
        </button>
      </div>
    </b-modal>
  </div>
</template>
<style scoped>
.height-36-px {
  height: 36px;
}

.gap-5 {
  gap: 0.5rem;
}

/* ========================================================= */
/* SUMMARY */
/* ========================================================= */

.reconciliation-summary {
  display: flex;
  align-items: stretch;

  border: 1px solid #cfe2ff;
  border-radius: 8px;
  background: #f8fbff;

  padding: 12px 15px;
  min-height: 92px;
}

.summary-item {
  display: flex;
  align-items: center;

  flex: 1;
  min-width: 0;

  padding: 0 15px;

  border-right: 1px solid #dbe7f5;
}

.summary-item:first-child {
  padding-left: 0;
}

.summary-progress {
  flex: 1.5;

  padding-left: 18px;

  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* ========================================================= */
/* ICON */
/* ========================================================= */

.summary-icon {
  width: 40px;
  height: 40px;
  min-width: 40px;

  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 20px;
  margin-right: 12px;
}

.icon-primary {
  background: #e8f1ff;
  color: #3867f0;
}

.icon-success {
  background: #e2f7ed;
  color: #20bf82;
}

.icon-warning {
  background: #e8f1ff;
  color: #3867f0;
}

/* ========================================================= */
/* CONTENT */
/* ========================================================= */

.summary-content {
  min-width: 0;
}

.summary-label {
  font-size: 12px;
  color: #53627a;
  font-weight: 500;
  line-height: 1.3;
}

.summary-value {
  font-size: 17px;
  line-height: 1.35;
  font-weight: 700;
  color: #15213b;

  white-space: nowrap;
}

.summary-percent {
  font-size: 11px;
  font-weight: 600;
  margin-top: 2px;
  line-height: 1.3;
}

.summary-progress-value {
  font-size: 17px;
  color: #15213b;
}

/* ========================================================= */
/* PROGRESS BAR */
/* ========================================================= */

.summary-progress .progress {
  height: 9px !important;

  background-color: #e1e6ed;
  border-radius: 10px;
  overflow: hidden;
}

.summary-progress .progress-bar {
  border-radius: 10px;
  transition: width 0.4s ease;
}

.summary-progress small {
  font-size: 10px;
  line-height: 1.3;
}

/* ========================================================= */
/* TABLET */
/* ========================================================= */

@media (max-width: 1100px) {
  .reconciliation-summary {
    flex-wrap: wrap;
    padding: 12px 15px;
  }

  .summary-item {
    flex: 1 1 33.33%;
    margin-bottom: 12px;
    padding: 0 12px;
  }

  .summary-item:first-child {
    padding-left: 0;
  }

  .summary-progress {
    flex: 1 1 100%;

    padding: 12px 0 0;

    border-top: 1px solid #dbe7f5;
  }
}

/* ========================================================= */
/* MOBILE */
/* ========================================================= */

@media (max-width: 768px) {
  .reconciliation-summary {
    display: block;
    padding: 5px 12px;
  }

  .summary-item {
    padding: 12px 0;

    border-right: 0;
    border-bottom: 1px solid #dbe7f5;

    margin-bottom: 0;
  }

  .summary-item:first-child {
    padding-left: 0;
  }

  .summary-progress {
    padding: 15px 0 8px;

    border-top: 0;
  }

  .summary-icon {
    width: 36px;
    height: 36px;
    min-width: 36px;

    margin-right: 10px;

    font-size: 18px;
  }

  .summary-label {
    font-size: 11px;
  }

  .summary-value {
    font-size: 15px;
  }

  .summary-percent {
    font-size: 10px;
  }

  .summary-progress-value {
    font-size: 16px;
  }

  .summary-progress .progress {
    height: 8px !important;
  }

  .summary-progress small {
    font-size: 9px;
  }
}

/* ========================================================= */
/* EXTRA SMALL */
/* ========================================================= */

@media (max-width: 575.98px) {
  .reconciliation-summary {
    padding: 4px 10px;
  }

  .summary-item {
    padding: 10px 0;
  }

  .summary-progress {
    padding: 13px 0 7px;
  }

  .summary-icon {
    width: 34px;
    height: 34px;
    min-width: 34px;

    margin-right: 9px;

    font-size: 17px;
  }

  .summary-value {
    font-size: 14px;
  }

  .summary-label {
    font-size: 11px;
  }
}
</style>
